// Helper function to remove trailing colons from titles
const cleanTitle = (title) => {
  return title ? title.replace(/:$/, "").trim() : title;
};

// Helper function for groupBy (similar to lodash)
const groupBy = (array, key) => {
  return array.reduce((result, item) => {
    const group = item[key];
    if (!result[group]) {
      result[group] = [];
    }
    result[group].push(item);
    return result;
  }, {});
};

export const processPermissionsData = (
  rawData,
  tabType,
  contentType = "general"
) => {
  const {
    formS_TABLE,
    excludE_GROUPS_TABLE,
    formS_GEN_DETAILS_TABLE,
    formS_TABS_TABLE,
    miscellaneouS_TABLE,
  } = rawData;

  const excludedColumns = new Set(
    excludE_GROUPS_TABLE?.map((item) => item.columnname) || []
  );

  switch (tabType) {
    case "forms":
      return processForms(formS_TABLE, excludedColumns, rawData);
    case "forms_content":
      return processFormsContent(
        formS_GEN_DETAILS_TABLE,
        formS_TABS_TABLE,
        contentType,
        rawData
      );
    case "tools":
      return processTools(rawData);
    case "reports":
      return processReports(rawData);
    case "miscellaneous":
      return processMiscellaneous(miscellaneouS_TABLE);
    default:
      return { categories: [] };
  }
};

// ==========================================
// FORMS PROCESSING
// ==========================================

const processForms = (formS_TABLE, excludedColumns, rawData) => {
  const sourceData = formS_TABLE.filter(
    (item) => !excludedColumns.has(item.columnname)
  );

  // Get top-level categories (parentitemid === null)
  const categories = sourceData
    .filter((item) => item.parentitemid === null)
    .sort((a, b) => a.cindex - b.cindex);

  const processedCategories = categories.map((category) => {
    // Get direct children of this category
    const categoryItems = sourceData
      .filter((item) => item.parentitemid === category.itemid)
      .sort((a, b) => a.cindex - b.cindex);

    const items = categoryItems.map((item) => {
      // Check if this item has children (like "Applications") by looking for items with this item as parentitemid
      const children = sourceData
        .filter((child) => child.parentitemid === item.itemid)
        .sort((a, b) => a.cindex - b.cindex);

      if (children.length > 0) {
        // This is a parent item (like "Applications") - create three-level structure
        // Children are Application Types that have this item as their parentitemid
        const childItems = children.map((child) => ({
          id: child.itemid,
          name: child.title,
          categoryId: category.itemid,
          parentId: item.itemid, // This links the child to its parent (Applications)
          isCategory: false,
          permissions: {
            search_view: Boolean(child.caN_SEARCH),
            create: Boolean(child.caN_CREATE),
            edit: Boolean(child.caN_EDIT),
            delete: Boolean(child.caN_DELETE),
            print: Boolean(child.canprint),
          },
        }));

        // Calculate parent (Applications) permissions based on all its children (Application Types)
        const parentPermissions = calculateFormsPermissions(
          childItems,
          rawData
        );

        return {
          id: item.itemid,
          name: item.title,
          categoryId: category.itemid,
          isCategory: false,
          isParent: true, // Mark as parent for rendering (this is the "Applications" row)
          permissions: parentPermissions, // Calculated from children
          items: childItems, // Array of Application Types
        };
      } else {
        // This is a regular item (no children) - direct form item
        return {
          id: item.itemid,
          name: item.title,
          categoryId: category.itemid,
          isCategory: false,
          isParent: false,
          permissions: {
            search_view: Boolean(item.caN_SEARCH),
            create: Boolean(item.caN_CREATE),
            edit: Boolean(item.caN_EDIT),
            delete: Boolean(item.caN_DELETE),
            print: Boolean(item.canprint),
          },
        };
      }
    });

    // Calculate category permissions based on all leaf-level items (Application Types)
    const categoryPermissions = calculateFormsCategoryPermissions(
      items,
      category,
      rawData
    );

    return {
      id: category.itemid,
      name: category.title,
      isCategory: true,
      permissions: categoryPermissions,
      items: items,
    };
  });

  return {
    categories: processedCategories,
    permissionTypes: ["search_view", "create", "edit", "delete", "print"],
  };
};

// Helper function to calculate forms permissions from children/items
const calculateFormsPermissions = (items, rawData) => {
  const permissionTypes = ["search_view", "create", "edit", "delete", "print"];
  const permissions = {};

  if (items.length === 0) {
    // No items, set all permissions to false
    permissionTypes.forEach((permType) => {
      permissions[permType] = false;
    });
    return permissions;
  }

  // Calculate each permission type based on all items
  permissionTypes.forEach((permType) => {
    if (permType === "print" && rawData?.formS_TABLE) {
      // Special handling for print - only consider printable items
      const printableItems = items.filter((item) => {
        const rawItem = rawData.formS_TABLE.find(
          (raw) => raw.itemid === item.id
        );
        return !rawItem || rawItem.canbeprinted !== 0;
      });

      if (printableItems.length === 0) {
        permissions[permType] = false;
      } else {
        permissions[permType] = printableItems.every(
          (item) => item.permissions[permType]
        );
      }
    } else {
      permissions[permType] = items.every((item) => item.permissions[permType]);
    }
  });

  return permissions;
};

// Helper function to calculate category permissions for forms (three-level aware)
const calculateFormsCategoryPermissions = (items, category, rawData) => {
  const permissionTypes = ["search_view", "create", "edit", "delete", "print"];
  const permissions = {};

  permissionTypes.forEach((permType) => {
    // Special handling for print permission
    if (permType === "print" && rawData?.formS_TABLE) {
      // Get all leaf-level items that are printable
      const allPrintableLeafItems = [];

      items.forEach((item) => {
        if (item.items && item.items.length > 0) {
          // Parent item with children - check each child
          item.items.forEach((child) => {
            // Check if this child can be printed
            const rawChild = rawData.formS_TABLE.find(
              (raw) => raw.itemid === child.id
            );
            // Only include if canbeprinted !== 0 (or if raw item not found, assume printable)
            if (!rawChild || rawChild.canbeprinted !== 0) {
              allPrintableLeafItems.push(child);
            }
          });
        } else {
          // Regular item - check if it can be printed
          const rawItem = rawData.formS_TABLE.find(
            (raw) => raw.itemid === item.id
          );
          // Only include if canbeprinted !== 0
          if (!rawItem || rawItem.canbeprinted !== 0) {
            allPrintableLeafItems.push(item);
          }
        }
      });

      if (allPrintableLeafItems.length === 0) {
        // No printable items, print should be false
        permissions[permType] = false;
      } else {
        // Calculate based on printable items only
        permissions[permType] = allPrintableLeafItems.every(
          (item) => item.permissions.print === true
        );
      }
    } else {
      // Normal calculation for other permissions
      // Get all leaf-level permissions (including nested items)
      const allLeafPermissions = [];

      items.forEach((item) => {
        if (item.items && item.items.length > 0) {
          // Parent item with children - add all children permissions
          item.items.forEach((child) => {
            allLeafPermissions.push(child.permissions[permType]);
          });
        } else {
          // Regular item - add its permission
          allLeafPermissions.push(item.permissions[permType]);
        }
      });

      if (allLeafPermissions.length === 0) {
        // No items, use category's raw permission
        const permissionMap = {
          search_view: "caN_SEARCH",
          create: "caN_CREATE",
          edit: "caN_EDIT",
          delete: "caN_DELETE",
          print: "canprint",
        };
        permissions[permType] = Boolean(category[permissionMap[permType]]);
      } else {
        // Calculate based on all leaf permissions
        permissions[permType] = allLeafPermissions.every((perm) => perm);
      }
    }
  });

  return permissions;
};

// ==========================================
// FORMS CONTENT PROCESSING
// ==========================================
const processFormsContent = (
  formS_GEN_DETAILS_TABLE,
  formS_TABS_TABLE,
  contentType,
  rawData
) => {
  // Choose the source data based on content type
  const sourceData =
    contentType === "general"
      ? formS_GEN_DETAILS_TABLE || []
      : formS_TABS_TABLE || [];

  if (!sourceData || sourceData.length === 0) {
    return {
      categories: [],
      permissionTypes: getFormsContentPermissionTypes(contentType),
      isFlat: false,
    };
  }

  // Group the source data by formid
  const formGroups = groupBy(sourceData, "formid");

  // Create a map to store our hierarchy (key by categoryId to avoid duplicates)
  const categoryMap = new Map();

  // Get all forms that have alldetailsformid and should appear in forms_content
  const formsWithDetails =
    (rawData.formS_TABLE || [])
      .filter(
        (item) =>
          item.alldetailsformid !== null &&
          item.alldetailsformid !== undefined &&
          item.alldetailsformid !== 0 && // Exclude items with 0 as alldetailsformid
          !item.columnname?.includes("_TOOLS") && // Exclude tools
          item.parentitemid !== null // Exclude top-level categories (they have null parentitemid)
      )
      // Sort top-level forms (use cindex for ordering; fallback to 0)
      .sort((a, b) => (a.cindex ?? 0) - (b.cindex ?? 0)) || [];

  console.log(
    `📋 Found ${formsWithDetails.length} forms with alldetailsformid`
  );

  // Process ALL forms that have alldetailsformid
  formsWithDetails.forEach((formItem) => {
    const formId = formItem.alldetailsformid;

    // Only process if this form actually has content items in the current content type
    // Ensure we sort those items by cindex (or ORDERINDEX if your source uses that)
    const items = (formGroups[formId] || []).slice().sort((a, b) => {
      const aIndex = a.cindex ?? a.ORDERINDEX ?? 0;
      const bIndex = b.cindex ?? b.ORDERINDEX ?? 0;
      return aIndex - bIndex;
    });
    if (items.length === 0) {
      return;
    }

    // Find the immediate parent of this form
    const immediateParent = rawData.formS_TABLE?.find(
      (item) => item.itemid === formItem.parentitemid
    );

    if (!immediateParent) {
      return;
    }

    // Check if the immediate parent has a parent (making it a middle-level item like Applications)
    const hasGrandparent = immediateParent.parentitemid !== null;

    // Find the top-level category
    let topCategory = immediateParent;
    if (hasGrandparent) {
      // If immediate parent has a parent, find the top-level category
      let current = immediateParent;
      while (current && current.parentitemid !== null) {
        const parent = rawData.formS_TABLE?.find(
          (item) => item.itemid === current.parentitemid
        );
        if (parent) {
          current = parent;
        } else {
          break;
        }
      }
      topCategory = current;
    }

    // Skip if the top category is General or Reports
    if (
      topCategory.columnname === "GENERALGROUP" ||
      topCategory.columnname === "REPORTS_GROUP"
    ) {
      return;
    }

    const categoryId = topCategory.itemid;
    const categoryName = topCategory.title;

    // Create or get the category
    if (!categoryMap.has(categoryId)) {
      categoryMap.set(categoryId, {
        id: categoryId,
        name: categoryName,
        isCategory: true,
        cindex: topCategory.cindex ?? 0,
        permissions: {},
        items: [], // These will be either forms directly or parent groups (like Applications)
      });
    }

    const category = categoryMap.get(categoryId);

    // Create the form item
    const formItemObj = {
      id: `form_${formItem.itemid}_${formId}`,
      name: formItem.title,
      formId: parseInt(formId),
      isCategory: false,
      cindex: formItem.cindex ?? 0, // preserve ordering if present
      permissions: {},
      items: [], // These will be the fields/tabs
    };

    // Process the individual items (fields/tabs) within this form
    items.forEach((item) => {
      const fieldItem = {
        id: item.itemid,
        name: cleanTitle(item.title),
        columnName: item.columnname,
        isCategory: false,
        cindex: item.cindex ?? item.ORDERINDEX ?? 0,
        permissions: buildFormsContentPermissions(item, contentType),
      };

      formItemObj.items.push(fieldItem);
    });

    // Sort the form's field items by cindex (leaf ordering)
    formItemObj.items.sort((a, b) => (a.cindex ?? 0) - (b.cindex ?? 0));

    // Calculate form-level permissions based on its items
    formItemObj.permissions = calculateFormsContentPermissions(
      formItemObj.items,
      contentType,
      rawData
    );

    // Now determine where to place this form
    if (hasGrandparent) {
      // This form belongs under a parent group (like Applications)
      const parentGroupId = `parent_${immediateParent.itemid}`;

      // Find or create the parent group in the category
      let parentGroup = category.items.find(
        (item) => item.id === parentGroupId
      );

      if (!parentGroup) {
        // Create the parent group (like Applications)
        parentGroup = {
          id: parentGroupId,
          name: immediateParent.title,
          isCategory: false,
          isParentGroup: true, // Special flag for parent groups
          cindex: immediateParent.cindex ?? 0,
          permissions: {},
          items: [], // Will contain the forms
        };
        category.items.push(parentGroup);
      }

      // Add the form to the parent group (keep its cindex for ordering)
      parentGroup.items.push(formItemObj);

      // Recalculate parent group permissions (based on its forms' fields)
      parentGroup.permissions = calculateFormsContentPermissions(
        parentGroup.items.flatMap((f) => f.items || []),
        contentType,
        rawData
      );

      // Sort forms inside the parent group by their cindex
      parentGroup.items.sort((a, b) => (a.cindex ?? 0) - (b.cindex ?? 0));
    } else {
      // This form goes directly under the category
      category.items.push(formItemObj);
    }
  });

  // Convert map to array and calculate category permissions
  const processedCategories = Array.from(categoryMap.values())
    // Sort categories by their cindex so order is stable
    .sort((a, b) => (a.cindex ?? 0) - (b.cindex ?? 0))
    .map((category) => {
      // Calculate category permissions based on ALL leaf items (fields/tabs)
      const allLeafPermissions = {};

      // Initialize permission arrays from permission types
      const permissionTypes = getFormsContentPermissionTypes(contentType);
      permissionTypes.forEach((permType) => {
        allLeafPermissions[permType] = [];
      });

      // Ensure parent groups' internal form arrays are sorted and fields sorted
      category.items.forEach((item) => {
        if (item.isParentGroup && item.items) {
          // Sort the parent group's forms by cindex (extra safety)
          item.items.sort((a, b) => (a.cindex ?? 0) - (b.cindex ?? 0));

          item.items.forEach((form) => {
            // Ensure fields inside forms are sorted (already done above, but safe)
            form.items.sort((x, y) => (x.cindex ?? 0) - (y.cindex ?? 0));
            if (form.items) {
              form.items.forEach((field) => {
                Object.keys(field.permissions).forEach((permType) => {
                  if (
                    permType === "print" &&
                    contentType === "tabs" &&
                    rawData?.formS_TABS_TABLE
                  ) {
                    // Check if this field can be printed
                    const rawField = rawData.formS_TABS_TABLE.find(
                      (raw) => raw.itemid === field.id
                    );
                    if (!rawField || rawField.canbeprinted !== 0) {
                      allLeafPermissions[permType].push(
                        field.permissions[permType]
                      );
                    }
                  } else {
                    allLeafPermissions[permType].push(
                      field.permissions[permType]
                    );
                  }
                });
              });
            }
          });
        } else if (item.items) {
          // This is a regular form directly under category
          // Sort fields as a safeguard
          item.items.sort((x, y) => (x.cindex ?? 0) - (y.cindex ?? 0));

          item.items.forEach((field) => {
            Object.keys(field.permissions).forEach((permType) => {
              if (
                permType === "print" &&
                contentType === "tabs" &&
                rawData?.formS_TABS_TABLE
              ) {
                // Check if this field can be printed
                const rawField = rawData.formS_TABS_TABLE.find(
                  (raw) => raw.itemid === field.id
                );
                if (!rawField || rawField.canbeprinted !== 0) {
                  allLeafPermissions[permType].push(
                    field.permissions[permType]
                  );
                }
              } else {
                allLeafPermissions[permType].push(field.permissions[permType]);
              }
            });
          });
        }
      });

      // Calculate category permissions
      const categoryPermissions = {};
      Object.keys(allLeafPermissions).forEach((permType) => {
        categoryPermissions[permType] =
          allLeafPermissions[permType].length > 0 &&
          allLeafPermissions[permType].every((val) => val);
      });

      category.permissions = categoryPermissions;
      return category;
    });

  return {
    categories: processedCategories,
    permissionTypes: getFormsContentPermissionTypes(contentType),
    isFlat: false,
  };
};

// NEW: Helper function to get permission types based on content type
const getFormsContentPermissionTypes = (contentType) => {
  if (contentType === "tabs") {
    return ["view", "edit", "print"]; // Add print for tabs
  }
  return ["view", "edit"]; // Default for general
};

// Helper function to get form info using alldetailsformid connection
const getFormInfoFromRawData = (formId, rawData) => {
  if (!rawData || !rawData.formS_TABLE) {
    return {
      name: `Form ${formId}`,
      categoryName: "Unknown",
      categoryId: null,
    };
  }

  // Find the form entry that has alldetailsformid matching our formId
  const formEntry = rawData.formS_TABLE.find(
    (item) => item.alldetailsformid === formId
  );

  if (!formEntry) {
    return {
      name: `Form ${formId}`,
      categoryName: "Unknown",
      categoryId: null,
    };
  }

  // Get the form name from the entry
  const formName = formEntry.title;

  // Find the parent category using parentitemid
  let categoryInfo = null;
  if (formEntry.parentitemid) {
    categoryInfo = rawData.formS_TABLE.find(
      (parent) => parent.itemid === formEntry.parentitemid
    );
  }

  // If no direct parent found, or parent is not a top-level category,
  // traverse up the hierarchy to find the top-level category
  let currentParent = categoryInfo;
  while (currentParent && currentParent.parentitemid !== null) {
    currentParent = rawData.formS_TABLE.find(
      (item) => item.itemid === currentParent.parentitemid
    );
  }

  const finalCategory = currentParent || categoryInfo;

  return {
    name: formName,
    categoryName: finalCategory ? finalCategory.title : "General",
    categoryId: finalCategory ? finalCategory.itemid : null,
  };
};

// Updated helper function to build permissions for individual items
const buildFormsContentPermissions = (item, contentType) => {
  const permissions = {
    view: Boolean(item.canview),
    edit: Boolean(item.canedit),
  };

  // Add print permission for tabs content type
  if (contentType === "tabs") {
    permissions.print = Boolean(item.canprint);
  }

  return permissions;
};

// Updated generic helper to calculate permissions from a list of items
const calculateFormsContentPermissions = (
  permissionItems,
  contentType,
  rawData
) => {
  if (permissionItems.length === 0) {
    const permissions = { view: false, edit: false };
    if (contentType === "tabs") {
      permissions.print = false;
    }
    return permissions;
  }

  const permissions = {};
  const permissionTypes = getFormsContentPermissionTypes(contentType);

  permissionTypes.forEach((permType) => {
    if (
      permType === "print" &&
      contentType === "tabs" &&
      rawData?.formS_TABS_TABLE
    ) {
      // Special handling for print in tabs - only consider printable items
      const printableItems = permissionItems.filter((item) => {
        const rawField = rawData.formS_TABS_TABLE.find(
          (raw) => raw.itemid === item.id
        );
        return !rawField || rawField.canbeprinted !== 0;
      });

      if (printableItems.length === 0) {
        permissions[permType] = false;
      } else {
        permissions[permType] = printableItems.every(
          (item) => item.permissions[permType]
        );
      }
    } else {
      permissions[permType] = permissionItems.every(
        (item) => item.permissions[permType]
      );
    }
  });

  return permissions;
};

// ==========================================
// TOOLS PROCESSING
// ==========================================

const processTools = (rawData) => {
  if (!rawData?.excludE_GROUPS_TABLE || !rawData?.formS_TABLE) {
    return { categories: [], permissionTypes: ["view_open", "edit"] };
  }

  const toolsColumnNames = getToolsColumnNames(rawData.excludE_GROUPS_TABLE);
  const mainParents = getMainParents(rawData.formS_TABLE, toolsColumnNames);
  const toolsItems = findToolsItems(rawData.formS_TABLE, mainParents);
  const processedCategories = buildToolsCategories(
    rawData.formS_TABLE,
    toolsItems
  );

  return {
    categories: processedCategories,
    permissionTypes: ["view_open", "edit"],
  };
};

const getToolsColumnNames = (excludE_GROUPS_TABLE) => {
  return excludE_GROUPS_TABLE
    .filter((item) => item.groupname === "Tools")
    .map((item) => item.columnname);
};

const getMainParents = (formS_TABLE, toolsColumnNames) => {
  return formS_TABLE.filter(
    (item) =>
      toolsColumnNames.includes(item.columnname) && item.parentitemid === null
  );
};

const findToolsItems = (formS_TABLE, mainParents) => {
  const toolsItems = [];
  mainParents.forEach((parent) => {
    const children = formS_TABLE.filter(
      (item) => item.parentitemid === parent.itemid
    );
    const toolsItem = children.find(
      (child) =>
        child.columnname === "TOOLSGROUP" ||
        child.title?.toLowerCase().includes("tools")
    );
    if (toolsItem) {
      toolsItems.push(toolsItem);
    }
  });
  return toolsItems;
};

const buildToolsCategories = (formS_TABLE, toolsItems) => {
  const processedCategories = [];

  toolsItems.forEach((toolsItem) => {
    const toolCategories = formS_TABLE.filter(
      (item) => item.parentitemid === toolsItem.itemid
    );

    toolCategories.forEach((category) => {
      const categoryItemsData = formS_TABLE.filter(
        (item) => item.parentitemid === category.itemid
      );

      const items = buildToolsItems(categoryItemsData, category.itemid);

      processedCategories.push({
        id: category.itemid,
        name: category.title,
        isCategory: true,
        permissions: calculateCategoryPermissions(items, category, "tools"),
        items: items,
      });
    });
  });

  return processedCategories;
};

const buildToolsItems = (categoryItemsData, categoryItemId) => {
  return categoryItemsData.map((item) => ({
    id: item.itemid,
    name: item.title,
    categoryId: categoryItemId,
    isCategory: false,
    permissions: {
      view_open: Boolean(item.caN_SEARCH),
      edit: Boolean(item.caN_SEARCH),
    },
  }));
};

// ==========================================
// REPORTS PROCESSING
// ==========================================

const processReports = (rawData) => {
  if (!rawData?.excludE_GROUPS_TABLE || !rawData?.formS_TABLE) {
    return { categories: [], permissionTypes: ["create_view"] };
  }

  const reportsParent = rawData.formS_TABLE.find(
    (item) => item.columnname === "REPORTS_GROUP" && item.parentitemid === null
  );

  if (!reportsParent) {
    return { categories: [], permissionTypes: ["create_view"] };
  }

  const reportCategories = rawData.formS_TABLE.filter(
    (item) => item.parentitemid === reportsParent.itemid
  );

  const processedCategories = reportCategories.map((category) => {
    const categoryItemsData = rawData.formS_TABLE.filter(
      (item) => item.parentitemid === category.itemid
    );

    const items = buildReportItems(categoryItemsData, category.itemid);

    return {
      id: category.itemid,
      name: category.title,
      isCategory: true,
      permissions: calculateCategoryPermissions(items, category, "reports"),
      items: items,
    };
  });

  return {
    categories: processedCategories,
    permissionTypes: ["create_view"],
  };
};

const buildReportItems = (categoryItemsData, categoryItemId) => {
  return categoryItemsData.map((item) => ({
    id: item.itemid,
    name: item.title,
    categoryId: categoryItemId,
    isCategory: false,
    permissions: {
      create_view: Boolean(item.caN_SEARCH), // Use only caN_SEARCH, not caN_CREATE
    },
  }));
};

// ==========================================
// MISCELLANEOUS PROCESSING
// ==========================================

const processMiscellaneous = (miscellaneouS_TABLE) => {
  if (!miscellaneouS_TABLE || miscellaneouS_TABLE.length === 0) {
    return {
      categories: [],
      items: [],
      permissionTypes: ["enabled"],
      isFlat: true,
    };
  }

  const miscItems = miscellaneouS_TABLE.map((item) => ({
    id: item.itemid,
    name: item.title,
    isCategory: false,
    permissions: {
      enabled: Boolean(item.caN_SEARCH),
    },
  }));

  return {
    categories: [],
    items: miscItems,
    permissionTypes: ["enabled"],
    isFlat: true,
  };
};

// ==========================================
// SHARED HELPER FUNCTIONS
// ==========================================

const calculateCategoryPermissions = (items, category, type) => {
  const permissionMap = {
    forms: {
      search_view: "caN_SEARCH",
      create: "caN_CREATE",
      edit: "caN_EDIT",
      delete: "caN_DELETE",
      print: "canprint",
    },
    tools: {
      view_open: "caN_SEARCH",
      edit: "caN_EDIT",
    },
    reports: {
      create_view: "caN_SEARCH",
    },
  };

  const permissions = {};
  const typeMap = permissionMap[type] || permissionMap.forms;

  Object.keys(typeMap).forEach((permType) => {
    if (items.length === 0) {
      permissions[permType] = Boolean(category[typeMap[permType]]);
    } else {
      permissions[permType] = items.every((item) => item.permissions[permType]);
    }
  });

  return permissions;
};
